import {
  type SavedView,
  type SavedViewConfig,
  createSavedView,
  deleteSavedView,
  listSavedViews,
} from '@/helpers/campaignManagement/savedViews';
import { useCallback, useEffect, useRef, useState } from 'react';

/** The current user's saved Campaign Management views (#5128). */
const useCampaignManagementSavedViews = () => {
  const [views, setViews] = useState<SavedView[]>([]);

  const latestRefresh = useRef(0);

  const refresh = useCallback(async () => {
    const id = ++latestRefresh.current;
    try {
      const next = await listSavedViews();
      // An older request finishing last must not overwrite a newer list.
      if (id === latestRefresh.current) setViews(next);
    } catch {
      // Keep the views already shown; a failed list must not break the page.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (name: string, config: SavedViewConfig) => {
      await createSavedView(name, config);
      await refresh();
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: number) => {
      await deleteSavedView(id);
      await refresh();
    },
    [refresh],
  );

  return { views, save, remove };
};

export default useCampaignManagementSavedViews;
